import React from 'react';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import moment from 'moment';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import { RRule } from 'rrule';
import RecurringPlanString from './RecurringPlanString';
import { getServiceEntity, areTimeSlotsEnabled } from '../helper';
import { allTimes } from '../QuickActions/ScheduleForm';

const useStyles = makeStyles(() => ({
  dateInput: {
    maxWidth: '115px',
    verticalAlign: 'baseline',
    marginLeft: '5px',
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      marginLeft: 0,
    },
  },
  topBottomMargin: {
    margin: '12px 0',
  },
}));

const RecurringPlanForm = ({
  isQuickAppointment = false,
  rruleAttributes,
  rruleCombined,
  onRruleChange,
  isEndDateInvalid,
  selectedServiceVehicleId = null,
  setSelectedServiceVehicleId = () => {},
  allServiceVehicles = [],
  timeSlotOptions = [],
  selectedTimeSlot,
  setSelectedTimeSlot = null,
}) => {
  const classes = useStyles();
  const { MONTHLY } = RRule;

  const isUsingTimeSlots = areTimeSlotsEnabled();

  const frequencyValue = () => {
    if (rruleAttributes.freq === MONTHLY) {
      return 'monthly';
    }
    if (rruleAttributes.interval === 1) {
      return 'weekly';
    }
    return 'biweekly';
  };

  const renderStartInput = () => (
    <Typography className={classes.topBottomMargin}>
      Starting On:{' '}
      {isQuickAppointment ? (
        <>{moment(rruleAttributes.dtstart).format('MM/DD/YYYY')}</>
      ) : (
        <TextField
          id="startDate"
          type="date"
          value={moment(rruleAttributes.dtstart).format('YYYY-MM-DD')}
          onChange={(e) =>
            onRruleChange('dtstart', new Date(`${e.target.value}T00:00:00`))
          }
          margin="dense"
          className={classes.dateInput}
        />
      )}
    </Typography>
  );

  const renderFrequencyInput = () => (
    <Typography className={classes.topBottomMargin}>
      Repeating:{' '}
      <Select
        size="large"
        value={frequencyValue()}
        onChange={(e) => onRruleChange('freq', e.target.value)}
      >
        <MenuItem key="weekly" value="weekly">
          Every Week
        </MenuItem>
        <MenuItem key="biweekly" value="biweekly">
          Every Other Week
        </MenuItem>
        <MenuItem key="monthly" value="monthly">
          Every Month
        </MenuItem>
      </Select>
    </Typography>
  );

  const renderEndInput = () => {
    let helperText = '';
    if (isEndDateInvalid) {
      if (rruleAttributes.dtstart > rruleAttributes.until) {
        helperText = 'End date cannot be before start date';
      } else
        helperText = 'End date cannot be more than 1 year after start date';
    }
    return (
      <Typography className={classes.topBottomMargin}>
        Ending On:{' '}
        <TextField
          id="endDate"
          type="date"
          value={moment(rruleAttributes.until).format('YYYY-MM-DD')}
          onChange={(e) =>
            onRruleChange(
              'until',
              new Date(`${e.target.value}T23:59:59.000+00:00`),
            )
          }
          margin="dense"
          className={classes.dateInput}
          error={isEndDateInvalid}
          helperText={helperText}
        />
      </Typography>
    );
  };

  const renderTimeSlotDropdown = () => {
    const isAvailable = timeSlotOptions?.length > 0;
    const options = isAvailable ? timeSlotOptions : allTimes();

    return (
      <Typography>
        <Typography className={classes.topBottomMargin}>
          At:{' '}
          <Select
            className="time-select"
            size="large"
            value={selectedTimeSlot}
            onChange={(e) => setSelectedTimeSlot(e.target.value)}
          >
            {options.map((t) => (
              <MenuItem key={`${t}-${isAvailable}`} value={t}>
                {t}
                {!isAvailable && ' - Unavailable'}
              </MenuItem>
            ))}
          </Select>
        </Typography>
      </Typography>
    );
  };

  // Do not render time slot dropdown in Quick Appointment, time selection will be separate
  if (isQuickAppointment) {
    return (
      <>
        <RecurringPlanString
          rruleCombined={rruleCombined}
          selectedTimeSlot={selectedTimeSlot}
        />
        {renderStartInput()}
        {renderFrequencyInput()}
        {renderEndInput()}
      </>
    );
  }

  return (
    <>
      <Typography>
        <Typography className={classes.topBottomMargin}>
          {getServiceEntity()}:{' '}
          <Select
            className="vehicle-select"
            size="large"
            value={selectedServiceVehicleId}
            onChange={(e) => setSelectedServiceVehicleId(e.target.value)}
          >
            {allServiceVehicles.map((sv) => (
              <MenuItem
                key={sv.service_vehicle_id}
                value={sv.service_vehicle_id}
              >
                {sv.name}
              </MenuItem>
            ))}
          </Select>
        </Typography>
      </Typography>
      {renderStartInput()}
      {renderFrequencyInput()}
      <Typography className={classes.topBottomMargin}>
        On:{' '}
        <strong>
          {rruleCombined
            .toText()
            .split(' on ')[1]
            ?.split(' until ')[0]
            ?.toUpperCase() || null}
        </strong>
      </Typography>
      {isUsingTimeSlots && renderTimeSlotDropdown()}
      {renderEndInput()}
    </>
  );
};

export default RecurringPlanForm;
